import { rem } from 'polished'
import { useCallback, useEffect, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { unique } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import Select from 'components/Common/Form/Input/Select'
import { mediaQueryUp, mediaQueryDown } from 'components/utils/breakpoint'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import ExperienceDateSelect from './ExperienceDateSelect'
import MarkdownRender from 'LuxKit/MarkdownRender'
import InputText from 'LuxKit/Typography/InputText'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { useIntl } from 'react-intl'
import { isValidTimeStr, timeStrToMs, toTwelveHourTime } from 'lib/datetime/time'
import { getDefaultTicketModes, getCombinedAndSortedCheckoutFields } from 'lib/experiences/ticketModeUtils'
import ExperienceTicketModeItemSelect from './ExperienceTicketModeItemSelect'

const PACKAGE_OPTION_KEY = 'package-option'

const BookingDateTimeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    grid-column-gap: ${rem(12)};

    > .full-width {
      grid-column: span 2;
    }
  }
`

const UniqueRegisterContainer = styled.div`
  display: flex;
  align-items: center;
  height: ${rem(40)};
`

const TicketOptionsTitle = styled(BodyText)`
  ${mediaQueryDown.tablet} {
    padding-top: ${rem(16)};
  }
`
interface Props {
  onTimeSelect?: (time: string) => void
  onDateSelect?: (date: App.ExperienceAvailabilityDate) => void
  onPickupPointSelect?: (pickupPoint?: App.ExperiencePickupPoint) => void
  onLocationSelect?: (location?: App.ExperienceRedemptionLocation) => void
  onLanguageSelect?: (language?: App.ExperienceLanguage) => void
  onTicketModeSelect?: (ticketModesMap?: Record<string, string>) => void
  experienceView: App.ExperienceBookingView
  allDates?: Array<App.ExperienceAvailabilityDate>
  availableTimes?: Array<App.ExperienceAvailabilityTimeSlot>
  defaultTimeSlot?: string
  selectedTimeSlot?: string
  defaultPickupPointId?: string
  defaultLanguageId?: string
  defaultRedemptionLocationId?: string
  defaultTicketModesMap?: Record<string, string>
  selectedDate?: string
  checkInDate?: string
  checkOutDate?: string
  removeLocationLabel?: boolean
  hideTicketModes?: boolean
  isCollinson?: boolean
  maxCheckInTime?: string
  maxCheckOutTime?: string
  checkInLabel?: string | React.ReactNode
  checkOutLabel?: string | React.ReactNode
}

function ExperienceTicketInputs(props: Props) {
  const {
    onTimeSelect,
    onDateSelect,
    onPickupPointSelect,
    onLocationSelect,
    onLanguageSelect,
    onTicketModeSelect,
    experienceView,
    allDates,
    availableTimes,
    defaultTimeSlot,
    selectedTimeSlot,
    defaultPickupPointId,
    defaultLanguageId,
    defaultRedemptionLocationId,
    defaultTicketModesMap,
    selectedDate,
    checkInDate,
    checkOutDate,
    removeLocationLabel = false,
    hideTicketModes = false,
    isCollinson = false,
    maxCheckInTime,
    maxCheckOutTime,
    checkInLabel,
    checkOutLabel,
  } = props

  const trackEvent = useTrackExperienceOfferEvent()
  const intl = useIntl()

  const languages = experienceView.originalExperience?.languages ?? []
  const allPickupPoints = useMemo(
    () => experienceView.originalExperience?.pickupPoints ?? [],
    [experienceView.originalExperience?.pickupPoints],
  )

  const [ticketModesSelected, setTicketModesSelected] = React.useState<Record<string, string>>(defaultTicketModesMap ?? {})
  const selectedPackage = ticketModesSelected[PACKAGE_OPTION_KEY]

  const pickupPoints = useMemo(() => {
    if (!selectedPackage) return allPickupPoints
    return allPickupPoints.filter(point =>
      !point.ticketModeKeys?.length || point.ticketModeKeys.includes(selectedPackage),
    )
  }, [allPickupPoints, selectedPackage])

  const combinedCheckoutFields = useMemo(() => {
    const showPickup = pickupPoints.length > 0
    return getCombinedAndSortedCheckoutFields(
      !hideTicketModes ? experienceView.originalExperience.ticketModes : undefined,
      showPickup,
    )
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- pickupPoints.length intentional (count only), handlers not closed over
  }, [experienceView.originalExperience.ticketModes, pickupPoints.length, hideTicketModes])

  useEffect(() => {
    const ticketModes = experienceView.originalExperience.ticketModes
    if (ticketModes && ticketModes.length > 1 && ticketModes.every(m => m.displayOrder == null)) {
      trackEvent('TICKET_MODES_DISPLAY_ORDER_MISSING')
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [experienceView.originalExperience.ticketModes])

  useEffect(() => {
    if (!onPickupPointSelect) return

    const currentPickupInList = defaultPickupPointId
      ? pickupPoints.find(p => p.id === defaultPickupPointId)
      : undefined

    if (currentPickupInList) return

    const pickupRequired = !experienceView.originalExperience?.pickupOptional

    // Auto-select when ticket-mode filter narrows pickups to a single required option,
    // otherwise the availability fetch is gated and the calendar never renders
    if (pickupPoints.length === 1 && pickupRequired && !defaultPickupPointId) {
      onPickupPointSelect(pickupPoints[0])
      return
    }

    if (defaultPickupPointId) {
      onPickupPointSelect(undefined)
    }
  }, [pickupPoints, defaultPickupPointId, onPickupPointSelect, experienceView.originalExperience?.pickupOptional])

  useEffect(() => {
    if (defaultTicketModesMap) {
      return
    }

    const res = getDefaultTicketModes(experienceView.originalExperience.ticketModes ?? [])
    onTicketModeChange(res)
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onTimeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    onTimeSelect?.(e.currentTarget.value)
    trackEvent('TIME_SLOT_SELECTED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onTimeSelect])

  const onPickupPointChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const pickupId = e.currentTarget.value
    const pickupPoint = experienceView.originalExperience.pickupPoints.find(p => p.id === pickupId)
    onPickupPointSelect?.(pickupPoint)
    trackEvent('PICKUP_LOCATION_SELECTED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onPickupPointSelect, experienceView])

  const onLocationChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const locationId = e.currentTarget.value
    const location = experienceView.originalExperience.redemptionLocations.find(rl => rl.id === locationId)
    onLocationSelect?.(location)
    trackEvent('REDEMPTION_LOCATION_SELECTED')
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onLocationSelect, experienceView])

  const onTicketModeChange = useCallback((newData: Record<string, string | undefined>) => {
    const updated = { ...ticketModesSelected }
    for (const key in newData) {
      if (!newData[key]) {
        delete updated[key]
      } else {
        updated[key] = newData[key]!
      }
    }
    setTicketModesSelected(updated)

    onTicketModeSelect?.(updated)
  }, [ticketModesSelected, onTicketModeSelect, setTicketModesSelected])

  const onLanguageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const languageId = e.currentTarget.value
    const language = experienceView.originalExperience.languages.find(l => l.id === languageId)
    onLanguageSelect?.(language)
    trackEvent('LANGUAGE_SELECTED')
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is a stable function
  }, [onLanguageSelect, experienceView])

  const uniqueTimeSlots = useMemo(() => {
    const isCheckInDateSelected = selectedDate === checkInDate
    const isCheckOutDateSelected = selectedDate === checkOutDate

    const isMaxCheckInTimeValid = !!maxCheckInTime && isValidTimeStr(maxCheckInTime)
    const isMaxCheckOutTimeValid = !!maxCheckOutTime && isValidTimeStr(maxCheckOutTime)

    const maxCheckInTimeMs = isCheckInDateSelected && isMaxCheckInTimeValid ? timeStrToMs(maxCheckInTime) : undefined
    const maxCheckOutTimeMs = isCheckOutDateSelected && isMaxCheckOutTimeValid ? timeStrToMs(maxCheckOutTime) : undefined

    const maxTimeMs = maxCheckInTimeMs || maxCheckOutTimeMs

    return unique((availableTimes ?? []).map(slot => slot.time))
      .map(time => ({
        time,
        disabled: !!maxTimeMs && timeStrToMs(time) > maxTimeMs,
      }))
      .sort((a, b) => timeStrToMs(a.time) - timeStrToMs(b.time))
  }, [availableTimes, checkInDate, checkOutDate, maxCheckInTime, maxCheckOutTime, selectedDate])

  useEffect(() => {
    if (uniqueTimeSlots.length === 1) {
      onTimeSelect?.(uniqueTimeSlots[0].time)
      trackEvent('TIME_SLOT_SELECTED_NON_INTERACTION')
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [onTimeSelect, uniqueTimeSlots])

  const availableDates = useMemo(() => allDates?.filter(date => !date.soldOut) ?? [], [allDates])

  const validatedSelectedDate = useMemo(
    () => {
      if (!selectedDate || !availableDates.length) return undefined
      return availableDates.some(availableDate => availableDate.day === selectedDate) ? selectedDate : undefined
    },
    [selectedDate, availableDates],
  )

  const showTimeSlots = experienceView.originalExperience.hasTimeslots && !experienceView.originalExperience.hideTimeSlots

  const redemptionLocation = experienceView.originalExperience?.redemptionLocations
  const showRedemptionLocation = redemptionLocation.length > 0
  const showReadOnlyRedemptionLocation = redemptionLocation.length === 1

  const pickupRequired = !experienceView.originalExperience?.pickupOptional
  const showPickupPoint = pickupPoints.length > 0
  const showReadOnlyPickupPoint = pickupPoints.length === 1 && pickupRequired
  const hasAnyInputs = languages.length > 0 || showPickupPoint || experienceView.originalExperience.hasCalendar || experienceView.originalExperience.hasTimeslots

  const checkAvailabilityText = intl.formatMessage({
    id: '-x693ADipumpELSl-h-o',
    defaultMessage: 'To check availability and prices, please let us know the following:',
    description: 'Instruction text asking users to provide details for experience booking',
  })

  const validForText = intl.formatMessage({
    id: 'k6Yi__gAT5KqDRR-nS_O',
    defaultMessage: 'Valid for <b>12 months</b> from the date of purchase',
    description: 'Validity period message for Collinson lounge passes',
  }, { b: (chunks: React.ReactNode) => <b>{chunks}</b> })

  const whereLabel = intl.formatMessage({
    id: 'J3qxV4VhQTs1AA3ggn8B',
    defaultMessage: 'Where',
    description: 'Label for the location information section in experience booking',
  })

  const pickupPointLabel = intl.formatMessage({
    id: 'P7IZuBq-2WRstM3SWPed',
    defaultMessage: 'Pickup point',
    description: 'Label for pickup point selection in experience booking',
  })

  const selectPickupPointPlaceholder = intl.formatMessage({
    id: 'M0-Ap94up5tuY7gpqd_1',
    defaultMessage: 'Select a pickup point',
    description: 'Placeholder for pickup point dropdown in experience booking',
  })

  const locationLabel = intl.formatMessage({
    id: '59MtJGNwEXA3lfrYMeks',
    defaultMessage: 'Location',
    description: 'Label for redemption location selection in experience booking',
  })

  const selectLocationPlaceholder = intl.formatMessage({
    id: 'kSsLya9FFPMpxuP6hvXK',
    defaultMessage: 'Select a location',
    description: 'Placeholder for redemption location dropdown in experience booking',
  })

  const languageLabel = intl.formatMessage({
    id: 'Tzxpqc7wM28ie4fC_dlc',
    defaultMessage: 'Language',
    description: 'Label for language selection in experience booking',
  })

  const selectLanguagePlaceholder = intl.formatMessage({
    id: 'sk9aVyQ6utQ_5u48gq5D',
    defaultMessage: 'Select a language',
    description: 'Placeholder for language dropdown in experience booking',
  })

  const dateLabel = intl.formatMessage({
    id: 'I9Yh6owNTO-5gtxo9pXt',
    defaultMessage: 'Date',
    description: 'Form field label for date selection in experience booking',
  })

  const timeLabel = intl.formatMessage({
    id: 'DY0hkgkIQ1cGqrnGW0eC',
    defaultMessage: 'Time',
    description: 'Label for time slot selection in experience booking',
  })

  const selectTimePlaceholder = intl.formatMessage({
    id: 'vlxt1PJ5JuJGYt3cHDSe',
    defaultMessage: 'Select a time',
    description: 'Placeholder for time slot dropdown in experience booking',
  })

  return (<BookingDateTimeContainer>
    {hasAnyInputs && <BodyText className="full-width" variant="large">
      {checkAvailabilityText}
    </BodyText>}
    {isCollinson && <BodyTextBlock className="full-width" variant="medium" style={{ marginTop: rem(4) }} startIcon={<LineCheckCircleIcon colour="success" size="S" />}>
      {validForText}
    </BodyTextBlock>}
    {!showPickupPoint && !showRedemptionLocation && experienceView.originalExperience.copy.whereText &&
      <div className="full-width">
        <BodyText variant="medium" weight="bold">{whereLabel}</BodyText>
        <MarkdownRender content={experienceView.originalExperience.copy.whereText} />
      </div>}

    {combinedCheckoutFields.map(item => {
      if (item.type === 'pickup') {
        return (
          <React.Fragment key="pickup">
            {showPickupPoint && <>
              {!showReadOnlyPickupPoint && <Select
                key={selectedPackage ?? 'all'}
                label={pickupPointLabel}
                placeholder={selectPickupPointPlaceholder}
                name="pickupPoint"
                className="full-width"
                onChange={onPickupPointChange}
                defaultValue={defaultPickupPointId ?? ((pickupPoints.length === 1 && pickupRequired) ? pickupPoints[0].id : undefined)}
                required={pickupRequired}
                noValidationSpacing
              >
                {!pickupRequired && <option>{intl.formatMessage({
                  id: 'fIxGBPSxlSG3Jgt9QZou',
                  defaultMessage: '-',
                  description: 'Default empty option for optional pickup point dropdown',
                })}
                </option>}
                {pickupPoints.map(point => <option key={point.id} value={point.id}>{point.name}</option>)}
              </Select>}
              {showReadOnlyPickupPoint && <div className="full-width">
                <InputText variant="text-regular">{pickupPointLabel}</InputText>
                <HiddenInput name="pickupPoint" value={pickupPoints[0].id} />
                {pickupPoints[0].name}
              </div>}
            </>}
          </React.Fragment>
        )
      } else if (item.type === 'ticketMode') {
        return (
          <ExperienceTicketModeItemSelect
            key={item.field.key}
            ticketModeField={item.field}
            selectedOptions={ticketModesSelected}
            onOptionSelect={onTicketModeChange}
          />
        )
      }
    })}

    {!showPickupPoint && showRedemptionLocation && <>
      {!showReadOnlyRedemptionLocation && <Select
        required
        label={locationLabel}
        noValidationSpacing
        className="full-width"
        name="redemptionLocation"
        onChange={onLocationChange}
        placeholder={selectLocationPlaceholder}
        defaultValue={defaultRedemptionLocationId ?? ((redemptionLocation.length === 1) ? redemptionLocation[0].id : undefined)}
      >
        {redemptionLocation.map(location => <option key={location.id} value={location.id}>{location.name}</option>)}
      </Select>}
      {showReadOnlyRedemptionLocation && <div className="full-width">
        <HiddenInput name="redemptionLocation" value={redemptionLocation[0].id} />
        {!removeLocationLabel && (<>
          <TicketOptionsTitle variant="medium" weight="bold">{locationLabel}</TicketOptionsTitle>
          {redemptionLocation[0].name}
        </>)}
      </div>}
    </>}

    {!experienceView.originalExperience.hasCalendar && selectedDate &&
      <input type="hidden" name="date" value={selectedDate} />}
    {experienceView.originalExperience.hasCalendar && !!allDates?.length && <ExperienceDateSelect
      availableDates={availableDates}
      selectedDate={validatedSelectedDate}
      defaultScrollDate={selectedDate}
      onDateChange={onDateSelect}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      noValidationSpacing
      name="date"
      label={dateLabel}
      required
      checkInLabel={checkInLabel}
      checkOutLabel={checkOutLabel}
    />}

    {languages.length > 1 && <Select
      label={languageLabel}
      name="language"
      placeholder={selectLanguagePlaceholder}
      defaultValue={defaultLanguageId ?? (languages.length === 1 ? languages[0].name : undefined)}
      required
      noValidationSpacing
      onChange={onLanguageChange}
    >
      {languages.map(lang => <option key={lang.id} value={lang.id}>{lang.name}</option>)}
    </Select>}
    {languages.length === 1 && <div>
      <HiddenInput name="language" value={languages[0].id} />
      <BodyText variant="medium">{languageLabel}</BodyText>
      <UniqueRegisterContainer>
        <BodyText variant="large">{languages[0].name}</BodyText>
      </UniqueRegisterContainer>
    </div>}

    {showTimeSlots && <div style={{ alignSelf: 'flex-end' }}>
      {uniqueTimeSlots.length > 1 && <Select
        label={timeLabel}
        name="time"
        placeholder={selectTimePlaceholder}
        data-testid="experience-time-select"
        onChange={onTimeChange}
        defaultValue={defaultTimeSlot}
        required
        noValidationSpacing
        value={selectedTimeSlot}
      >
        {uniqueTimeSlots.map(slot => <option key={slot.time} value={slot.time} disabled={slot.disabled}>
          {toTwelveHourTime(slot.time)}
        </option>)}
      </Select>}
      {uniqueTimeSlots.length === 1 && <>
        <HiddenInput name="time" value={uniqueTimeSlots[0].time} />
        <BodyText variant="medium">{timeLabel}</BodyText>
        <UniqueRegisterContainer>
          <BodyText variant="large">{intl.formatMessage(
            {
              id: 'Cx8qYCbqzvaRJ8wtu1J8',
              defaultMessage: 'Only available at {time}',
              description: 'Text showing the single available time slot for an experience',
            },
            { time: toTwelveHourTime(uniqueTimeSlots[0].time) },
          )}
          </BodyText>
        </UniqueRegisterContainer>
      </>}
    </div>}

    {!showTimeSlots && uniqueTimeSlots.length > 0 && (
      <HiddenInput name="time" value={uniqueTimeSlots[0].time} />
    )}

  </BookingDateTimeContainer>
  )
}

export default ExperienceTicketInputs
